import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

function locationPath(location) {
  return `${location.pathname || ""}${location.search || ""}${location.hash || ""}`;
}

export function useFarmMapPublishGuard({ onPublish }) {
  const history = useHistory();
  const [hasUnpublishedChanges, setHasUnpublishedChanges] = useState(false);
  const [leavePromptOpen, setLeavePromptOpen] = useState(false);
  const [pendingLocation, setPendingLocation] = useState(null);
  const [leaveActionLoading, setLeaveActionLoading] = useState(false);
  const unblockRef = useRef(null);
  const onPublishRef = useRef(onPublish);

  useEffect(() => {
    onPublishRef.current = onPublish;
  }, [onPublish]);

  const markUnpublished = useCallback(() => {
    setHasUnpublishedChanges(true);
  }, []);

  const clearUnpublished = useCallback(() => {
    setHasUnpublishedChanges(false);
  }, []);

  useEffect(() => {
    if (!hasUnpublishedChanges) {
      if (unblockRef.current) {
        unblockRef.current();
        unblockRef.current = null;
      }
      return undefined;
    }

    const unblock = history.block((location) => {
      const nextPath = locationPath(location);
      const currentPath = locationPath(history.location);
      if (nextPath === currentPath) {
        return undefined;
      }
      setPendingLocation(location);
      setLeavePromptOpen(true);
      return false;
    });

    unblockRef.current = unblock;

    return () => {
      unblock();
      if (unblockRef.current === unblock) {
        unblockRef.current = null;
      }
    };
  }, [hasUnpublishedChanges, history]);

  useEffect(() => {
    const onBeforeUnload = (event) => {
      if (!hasUnpublishedChanges) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [hasUnpublishedChanges]);

  const stayOnPage = useCallback(() => {
    setLeavePromptOpen(false);
    setPendingLocation(null);
  }, []);

  const leaveWithoutSync = useCallback(() => {
    const location = pendingLocation;
    setHasUnpublishedChanges(false);
    setLeavePromptOpen(false);
    setPendingLocation(null);
    if (!location) return;
    const target = locationPath(location);
    window.setTimeout(() => {
      history.push(target);
    }, 0);
  }, [history, pendingLocation]);

  const syncAndLeave = useCallback(async () => {
    if (!pendingLocation) return;
    setLeaveActionLoading(true);
    try {
      await onPublishRef.current();
      const location = pendingLocation;
      setHasUnpublishedChanges(false);
      setLeavePromptOpen(false);
      setPendingLocation(null);
      const target = locationPath(location);
      window.setTimeout(() => {
        history.push(target);
      }, 0);
    } finally {
      setLeaveActionLoading(false);
    }
  }, [pendingLocation]);

  return {
    hasUnpublishedChanges,
    markUnpublished,
    clearUnpublished,
    leavePromptOpen,
    stayOnPage,
    leaveWithoutSync,
    syncAndLeave,
    leaveActionLoading,
  };
}
